import "@assets/css/mode_tool/panel.css";
import ModeToolHeader from "@components/mode_tool/header";
import ModeToolModeList from "@components/mode_tool/modeList";
import ModeToolPointsList from "@components/mode_tool/pointsList";
import useStartPointLanguages from "@hooks/start_point/useStartPointLanguages";
import { useEffect, useMemo, useState } from "react";

function readLocalizedValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") return entry.value || "";
  return "";
}

function resolveSelectedStartPointName(point = {}, activeLanguageIso = "") {
  const localizedName =
    readLocalizedValue(point?.name?.[activeLanguageIso]) ||
    readLocalizedValue(point?.name?.en) ||
    readLocalizedValue(point?.name?.es) ||
    "";
  return (
    String(localizedName || "").trim() ||
    String(
      point?.zoneName ||
        point?.zone ||
        point?.placeName ||
        point?.locationName ||
        point?.id ||
        "",
    ).trim()
  );
}

function ModeRouteToolPanel({
  startPointMode,
  selectedStartPoint,
  modeOptions,
  selectedMode,
  isPickingModePoints,
  modePointCount,
  modePoints,
  selectedPointId,
  onSelectMode,
  onTogglePickingModePoints,
  onStopWalkingRouteShapeEdit,
  onSelectPoint,
  languages,
  onCommitPoint,
  onUploadPointMedia,
  onDeletePoint,
  onSaveModeRedux,
  canSaveModePoints,
  modeValidationHint,
  saveModePointsHint,
  onCloseTool,
  walkingRouteKeys,
  selectedWalkingRouteKey,
  walkingRouteMetaByKey,
  isWalkingRouteShapeEditEnabled,
  onSelectWalkingRoute,
  onAddWalkingRoute,
  onChangeWalkingRouteName,
  onToggleWalkingRouteShapeEdit,
  onDeleteWalkingRoute,
  onActiveLanguageChange,
  onImportJson,
  isImportingJson,
  onExportJson,
  canExportJson = false,
  importJsonMessage = "",
  importJsonError = "",
}) {
  const languageList = useStartPointLanguages(languages);
  const [activeLanguageIso, setActiveLanguageIso] = useState("");

  useEffect(() => {
    if (!languageList.length) {
      setActiveLanguageIso("");
      return;
    }
    setActiveLanguageIso((currentIso) => {
      if (languageList.some((language) => language.iso === currentIso)) {
        return currentIso;
      }
      return languageList[0].iso;
    });
  }, [languageList]);

  const visibleLanguageIso = useMemo(() => {
    if (languageList.some((language) => language.iso === activeLanguageIso)) {
      return activeLanguageIso;
    }
    return languageList[0]?.iso || "";
  }, [activeLanguageIso, languageList]);

  const selectedStartPointName = useMemo(
    () =>
      resolveSelectedStartPointName(selectedStartPoint || {}, visibleLanguageIso),
    [selectedStartPoint, visibleLanguageIso],
  );

  useEffect(() => {
    onActiveLanguageChange?.(visibleLanguageIso);
  }, [onActiveLanguageChange, visibleLanguageIso]);

  if (!startPointMode) return null;

  return (
    <aside
      className="mode-route-tool-overlay"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <ModeToolHeader
        onCloseTool={onCloseTool}
        selectedMode={selectedMode}
        isPickingModePoints={isPickingModePoints}
        onTogglePickingModePoints={onTogglePickingModePoints}
        onStopWalkingRouteShapeEdit={onStopWalkingRouteShapeEdit}
        selectedStartPointName={selectedStartPointName}
        languageList={languageList}
        activeLanguageIso={visibleLanguageIso}
        onChangeLanguage={setActiveLanguageIso}
        onImportJson={onImportJson}
        isImportingJson={isImportingJson}
        onExportJson={onExportJson}
        canExportJson={canExportJson}
      />
      {importJsonError ? (
        <p className="message-error mode-route-tool-import-message">
          {importJsonError}
        </p>
      ) : null}
      {importJsonMessage ? (
        <p
          className={`mode-route-tool-help mode-route-tool-import-message ${
            isImportingJson ? "is-loading" : "is-success"
          }`}
        >
          {isImportingJson ? (
            <span className="mode-route-tool-btn-spinner" />
          ) : null}
          <span>{importJsonMessage}</span>
        </p>
      ) : null}
      {/*       <p className="mode-route-tool-help">Select route points and add the details you need for each route type.</p>
       */}{" "}
      <ModeToolModeList
        modeOptions={modeOptions}
        selectedMode={selectedMode}
        modePointCount={modePointCount}
        onSelectMode={onSelectMode}
        onStopWalkingRouteShapeEdit={onStopWalkingRouteShapeEdit}
      />
      <ModeToolPointsList
        selectedMode={selectedMode}
        modePoints={modePoints}
        selectedPointId={selectedPointId}
        onSelectPoint={onSelectPoint}
        languages={languages}
        activeLanguageIso={visibleLanguageIso}
        onCommitPoint={onCommitPoint}
        onUploadPointMedia={onUploadPointMedia}
        onDeletePoint={onDeletePoint}
        onSaveModeRedux={onSaveModeRedux}
        canSaveModePoints={canSaveModePoints}
        modeValidationHint={modeValidationHint}
        saveModePointsHint={saveModePointsHint}
        walkingRouteKeys={walkingRouteKeys}
        selectedWalkingRouteKey={selectedWalkingRouteKey}
        walkingRouteMetaByKey={walkingRouteMetaByKey}
        isWalkingRouteShapeEditEnabled={isWalkingRouteShapeEditEnabled}
        onSelectWalkingRoute={onSelectWalkingRoute}
        onAddWalkingRoute={onAddWalkingRoute}
        onChangeWalkingRouteName={onChangeWalkingRouteName}
        onToggleWalkingRouteShapeEdit={onToggleWalkingRouteShapeEdit}
        onStopWalkingRouteShapeEdit={onStopWalkingRouteShapeEdit}
        onDeleteWalkingRoute={onDeleteWalkingRoute}
      />
    </aside>
  );
}

export default ModeRouteToolPanel;
